import { ConnectionStatus, AuthenticateResponse, OAuthCallbackResponse } from './quickbooksType';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const CALLBACK_BASE_URL = process.env.REACT_APP_BASE_URL;

class QuickbooksApi {
  // Modified authenticate method to work with popup flow
  static async authenticate(account_id: string, front_href: string): Promise<void> {
    try {
      // Call the authenticate endpoint with front_href parameter
      const response = await fetch(
        `${API_BASE_URL}/quickbooks_connections/${account_id}/authenticate?front_href=${CALLBACK_BASE_URL}/${encodeURIComponent(front_href)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.status !== 200) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start authentication');
      }

      const { redirect_url, front_href: returned_front_href } = await response.json();

      // Redirect current popup window to QuickBooks OAuth
      window.location.href = redirect_url;
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to start QuickBooks auth');
    }
  }

  // Alternative authenticate method that returns the redirect URL instead of redirecting
  // You can use this if you want more control over the redirect
  static async getAuthenticateUrl(account_id: string, front_href: string): Promise<AuthenticateResponse> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/quickbooks_connections/${account_id}/authenticate?front_href=${CALLBACK_BASE_URL}/${encodeURIComponent(front_href)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.status !== 200) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start authentication');
      }

      const result = await response.json();
      return {
        success: true,
        redirect_url: result.redirect_url,
        state: result.state || '',
        front_href: result.front_href || front_href
      };
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to start QuickBooks auth');
    }
  }

  // Handle OAuth callback - call this when the popup receives the callback
  static async handleOAuthCallback(code: string, state: string, realmId: string): Promise<OAuthCallbackResponse> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/quickbooks_connections/oauth_callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}&realmId=${encodeURIComponent(realmId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        return result;
      } else {
        throw new Error(result.message || 'OAuth callback failed');
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to process OAuth callback');
    }
  }

  static async checkConnectionStatus(account_id: string): Promise<ConnectionStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/quickbooks_connections/${account_id}/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.status === 200) {
        return await response.json();
      } else if (response.status === 401) {
        throw new Error('Session expired. Please refresh your browser.');
      } else {
        const responseBody = await response.json();
        throw new Error(`${responseBody.error}, status: ${response.status}`);
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error('Failed to check connection status');
    }
  }

  static async sendInvoiceToQuickbooks(customerId: number | string, invoiceData: any, accountId: number | string, invoiceId?: number | string) {
    try {
      const payload: any = {
        invoice_data: invoiceData
      };
      
      // Add invoice_id if provided
      if (invoiceId) {
        payload.invoice_id = invoiceId;
      }

      const response = await fetch(`${API_BASE_URL}/quickbooks_invoices/${customerId}/send_invoice?account_id=${accountId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'include', // Include cookies in the request
      });

      if (response.ok) {
        return await response.json();
      }
      else if (response.status === 401) {
        throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
      }
      else {
        // Handle error response
        const errorBody = await response.text();
        let errorMessage = `Error ${response.status}: ${response.statusText}`;
        if (errorBody) {
          errorMessage += ` - ${errorBody}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to send invoice to QuickBooks: ${error.message}`);
      } else {
        throw new Error('Failed to send invoice to QuickBooks: An unknown error occurred.');
      }
    }
  }

}

export default QuickbooksApi;